/* eslint-disable @eslint-community/eslint-comments/disable-enable-pair -- eslint-disable -- need to keep code in this format */
/* eslint-disable @eslint-community/eslint-comments/no-unlimited-disable -- eslint-disable -- need to keep code in this format */
import React, { StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { setAppLocales, allLocales, i18n } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router, AuthRoute } from '@fs/zion-router'
import { appPath, sentryDSN, baseUrl, mergeSentryConfig, isLoggedIn as userIsLoggedIn } from '@fs/zion-config'
import { AppLayout, colors } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import App from './App'
import { getLangPrefixFromPathname } from './lib/helpers'
import * as serviceWorker from './serviceWorker'
import './locales'

const EmptyLoading = () => <div />

const devEnv = process.env.NODE_ENV === 'development'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = devEnv ? import('./dev.flags').then((m) => m.default) : undefined

const mockActivities = devEnv ? import('./dev.activities').then((m) => m.default) : undefined

/* Sentry initialization.
 * Sentry is disabled locally by default (SENTRY_DSN is unset and SENTRY_DISABLED=true in .env).
 * Snow runs locally via setupProxy.js and populates window.SERVER_DATA on every page load, so to enable Sentry in development, run:
 * SENTRY_DSN=<your-dsn> SENTRY_DISABLED=false npm start
 * Events will log under the "local" environment (TARGET_ENV in .env).
 * Additional debugging is gated conditionally by devEnv.
 */
if (sentryDSN) {
  /* Sample rates and strict filtering are hardcoded because CRA bakes REACT_APP_* env vars at BUILD time via webpack DefinePlugin — they cannot be changed at runtime via server config. To adjust these values, change them here and redeploy. */
  const sentryConfig = mergeSentryConfig({
    attachStacktrace: devEnv,
    debug: devEnv, // Log Sentry SDK diagnostics to the console in development.
    sampleRate: devEnv ? 1 : 0.05, // 0.0–1.0 fraction. 100% of events locally.
    strictFiltering: true, // Enhanced filtering via @fs/zion-config (https://github.com/fs-webdev/zion/pull/13771)
    ignoreErrors: [
      /.*\.flat is not a function.*/, // Ancient browser compatibility errors.
      /.*NotReadableError.*/, // Unactionable IndexedDB corruption errors.
      /.*Data lost due to missing file.*/, // Unactionable IndexedDB corruption errors.
    ],
  })
  // eslint-disable-next-line no-console -- dev-locked debugging statement of value to the *browser* console.
  devEnv && console.log('Sentry config:', sentryConfig)

  Sentry.init(sentryConfig)
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''
// We cannot just handle /temple, as there are some apps that use temple as a base (e.g. '/temple/guide-me/family-name-assist')
const routesToHandle = [
  'temple/reservations/',
  'temple/ordinances-ready/',
  'temple/completed/',
  'temple/receive-a-name/',
  'temple/appointment/',
  'temple/shared/',
  'temple/groups/',
  'temple/add/',
]
// Add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  setAppLocales(allLocales)

  if (!userIsLoggedIn && window?.location?.pathname?.includes('ordinances-ready')) {
    const langPrefix = getLangPrefixFromPathname(window.location.pathname, i18n.language)
    window.location.replace(`${baseUrl}${langPrefix}/church/ordinances-ready`)
    return null
  }

  return (
    <Router basename={base} handledExternalRoutes={routesToHandle}>
      <Root footer={{ hidden: true }} analytics>
        <AppLayout fullWidth backgroundColor={colors.gray02}>
          {/* AuthRoute here makes sure there's a user for children components */}
          <AuthRoute exact route="*">
            <App />
          </AuthRoute>
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} LoadingFallback={EmptyLoading} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()

/* eslint-disable -- need to keep code in this format */
// <!-- begin usabilla live embed code -->
/*{literal}<![CDATA[*/window.lightningjs||function(c){function g(b,d){d&&(d+=(/\?/.test(d)?"&":"?")+"lv=1");c[b]||function(){var i=window,h=document,j=b,g=h.location.protocol,l="load",k=0;(function(){function b(){a.P(l);a.w=1;c[j]("_load")}c[j]=function(){function m(){m.id=e;return c[j].apply(m,arguments)}var b,e=++k;b=this&&this!=i?this.id||0:0;(a.s=a.s||[]).push([e,b,arguments]);m.then=function(b,c,h){var d=a.fh[e]=a.fh[e]||[],j=a.eh[e]=a.eh[e]||[],f=a.ph[e]=a.ph[e]||[];b&&d.push(b);c&&j.push(c);h&&f.push(h);return m};return m};var a=c[j]._={};a.fh={};a.eh={};a.ph={};a.l=d?d.replace(/^\/\//,(g=="https:"?g:"http:")+"//"):d;a.p={0:+new Date};a.P=function(b){a.p[b]=new Date-a.p[0]};a.w&&b();i.addEventListener?i.addEventListener(l,b,!1):i.attachEvent("on"+l,b);var q=function(){function b(){return["<head></head><",c,' onload="var d=',n,";d.getElementsByTagName('head')[0].",d,"(d.",g,"('script')).",i,"='",a.l,"'\"></",c,">"].join("")}var c="body",e=h[c];if(!e)return setTimeout(q,100);a.P(1);var d="appendChild",g="createElement",i="src",k=h[g]("div"),l=k[d](h[g]("div")),f=h[g]("iframe"),n="document",p;k.style.display="none";e.insertBefore(k,e.firstChild).id=o+"-"+j;f.frameBorder="0";f.id=o+"-frame-"+j;/MSIE[ ]+6/.test(navigator.userAgent)&&(f[i]="javascript:false");f.allowTransparency="true";l[d](f);try{f.contentWindow[n].open()}catch(s){a.domain=h.domain,p="javascript:var d="+n+".open();d.domain='"+h.domain+"';",f[i]=p+"void(0);"}try{var r=f.contentWindow[n];r.write(b());r.close()}catch(t){f[i]=p+'d.write("'+b().replace(/"/g,String.fromCharCode(92)+'"')+'");d.close();'}a.P(2)};a.l&&setTimeout(q,0)})()}();c[b].lv="1";return c[b]}var o="lightningjs",k=window[o]=g(o);k.require=g;k.modules=c}({});
window.usabilla_live = lightningjs.require("usabilla_live", "//w.usabilla.com/9e116afd8c6f.js");
/*]]>{/literal}*/
// <!-- end usabilla live embed code -->

// Turning off the Usabilla feedback button by default to only show on pages with shared family groups
window.usabilla_live('hide')
